import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import {
  Avatar,
  Box,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { handleErrorMessage } from "../../../helpers/methods";
import {
  AstroBaseMapKey,
  AstroCityHit,
  AstroFeatureProperties,
  AstroLineCategory,
  AstroLineLayerVisibility,
  AstroLinesResponse,
} from "../../../models/interfaces";
import history from "../../../utils/history";
import { getProfile } from "../../Dashboard/DashboardService";
import astrocartographyStyles from "./Astrocartography.styles";
import { getAstrocartographyLines } from "./AstrocartographyService";
import { ASTRO_ACCENTS, createAstroTheme } from "./astroTheme";
import AstroControlDock, {
  AstroSectionId,
} from "./components/AstroControlDock";
import AstroMapStage from "./components/AstroMapStage";
import useAstrocartographyMap from "./hooks/useAstrocartographyMap";
import useFullscreen from "./hooks/useFullscreen";
import astroCities from "./utils/astroCities";
import {
  ANY_ANGLE,
  DEFAULT_BASE_MAP,
  DEFAULT_PLANET,
  RADIUS_DEFAULT_KM,
} from "./utils/astrocartographyConstants";
import { buildCityPopup, buildLinePopup } from "./utils/astrocartographyPopup";
import {
  buildLines,
  collectPlanetColors,
  collectPlanets,
  findCitiesNearLines,
  groupMainSegmentsByPlanet,
  selectSegments,
} from "./utils/astrocartographyUtils";

/** The section open on first paint; the rest stay folded to favour the map. */
const DEFAULT_SECTION: AstroSectionId = "cities";

const DEFAULT_LAYER_VISIBILITY: AstroLineLayerVisibility = {
  main: true,
  harm: true,
  // Disharmonious aspects start hidden to keep the map readable.
  disharm: false,
};

const Astrocartography = () => {
  const classes = astrocartographyStyles;
  const { t, i18n } = useTranslation();

  const [profiles, setProfiles] = useState<any[]>([]);
  const [selectedProfile, setSelectedProfile] = useState<any>(null);
  const [isProfilesLoading, setIsProfilesLoading] = useState<boolean>(false);
  const [isLinesLoading, setIsLinesLoading] = useState<boolean>(false);
  const [linesError, setLinesError] = useState<string>("");
  const [hasLinesError, setHasLinesError] = useState<boolean>(false);
  const [data, setData] = useState<AstroLinesResponse | null>(null);

  const [baseMapKey, setBaseMapKey] =
    useState<AstroBaseMapKey>(DEFAULT_BASE_MAP);
  const [layerVisibility, setLayerVisibility] =
    useState<AstroLineLayerVisibility>(DEFAULT_LAYER_VISIBILITY);
  const [showLabels, setShowLabels] = useState<boolean>(true);
  const [planetVisibility, setPlanetVisibility] = useState<
    Record<string, boolean>
  >({});

  const [selectedPlanet, setSelectedPlanet] = useState<string>("");
  const [selectedAngle, setSelectedAngle] = useState<string>(ANY_ANGLE);
  const [radiusKm, setRadiusKm] = useState<number>(RADIUS_DEFAULT_KM);

  const [cityHits, setCityHits] = useState<AstroCityHit[] | undefined>();
  const [searchedPlanet, setSearchedPlanet] = useState<string>("");
  const [searchedAngle, setSearchedAngle] = useState<string>(ANY_ANGLE);
  const [searchedRadiusKm, setSearchedRadiusKm] =
    useState<number>(RADIUS_DEFAULT_KM);

  const [pendingSearch, setPendingSearch] = useState<boolean>(false);

  // Presentation-only state for the control drawer.
  const [isDockCollapsed, setIsDockCollapsed] = useState<boolean>(false);
  const [expandedSection, setExpandedSection] = useState<
    AstroSectionId | false
  >(DEFAULT_SECTION);

  /** Guards against a slow response for a previously selected profile landing last. */
  const requestIdRef = useRef<number>(0);
  const stageRef = useRef<HTMLDivElement>(null);

  const lines = useMemo(
    () => (data?.features ? buildLines(data.features) : []),
    [data],
  );
  const planets = useMemo(() => collectPlanets(lines), [lines]);
  const planetColors = useMemo(() => collectPlanetColors(lines), [lines]);
  const segmentsByPlanet = useMemo(
    () => groupMainSegmentsByPlanet(lines),
    [lines],
  );

  const renderPopup = useCallback(
    (properties: AstroFeatureProperties) =>
      buildLinePopup(properties, i18n.language, t),
    [i18n.language, t],
  );

  const renderCityPopup = useCallback(
    (city: AstroCityHit, planet: string) => buildCityPopup(city, planet, t),
    [t],
  );

  const handleMainLineClick = useCallback((planet: string, angle: string) => {
    setSelectedPlanet(planet);
    setSelectedAngle(angle);
    // The search itself runs from the effect below, once the selection lands.
    setPendingSearch(true);
  }, []);

  const { containerRef, showCityResults, clearCityResults, invalidateSize } =
    useAstrocartographyMap({
      lines,
      planetColors,
      birthPlace: data?.birthPlace,
      baseMapKey,
      planetVisibility,
      layerVisibility,
      showLabels,
      language: i18n.language,
      renderPopup,
      birthPlaceLabel: t("astroBirthPlace"),
      renderCityPopup,
      onMainLineClick: handleMainLineClick,
    });

  /* ------------------------------- profiles ------------------------------- */

  useEffect(() => {
    const loadProfiles = async () => {
      try {
        setIsProfilesLoading(true);
        const response = await getProfile();
        setProfiles(response || []);
      } catch (error: any) {
        handleErrorMessage(error);
      } finally {
        setIsProfilesLoading(false);
      }
    };
    loadProfiles();
  }, []);

  /* ------------------------- astrocartography lines ----------------------- */

  const resetLineState = useCallback(() => {
    setData(null);
    setCityHits(undefined);
    setPlanetVisibility({});
    setSelectedPlanet("");
    clearCityResults();
  }, [clearCityResults]);

  useEffect(() => {
    const profileId = selectedProfile?.profileId;
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    resetLineState();
    setLinesError("");
    setHasLinesError(false);

    if (!profileId) {
      setIsLinesLoading(false);
      return;
    }

    const loadLines = async () => {
      try {
        setIsLinesLoading(true);
        const response = await getAstrocartographyLines(profileId);
        if (requestIdRef.current !== requestId) return;
        setData(response);
      } catch (error: any) {
        if (requestIdRef.current !== requestId) return;
        setHasLinesError(true);
        setLinesError(error?.errorMessage || "");
        handleErrorMessage(error);
      } finally {
        if (requestIdRef.current === requestId) setIsLinesLoading(false);
      }
    };
    loadLines();
  }, [selectedProfile, resetLineState]);

  /* --------------------------- city line search --------------------------- */

  const runSearch = useCallback(
    (planet: string, angle: string, radius: number) => {
      setSearchedPlanet(planet);
      setSearchedAngle(angle);
      setSearchedRadiusKm(radius);

      if (!planet) {
        setCityHits(undefined);
        clearCityResults();
        return;
      }
      const segments = selectSegments(segmentsByPlanet, planet, angle);
      const hits = findCitiesNearLines(astroCities, segments, radius);
      setCityHits(hits);
      showCityResults(hits, planet, segments);
    },
    [segmentsByPlanet, showCityResults, clearCityResults],
  );

  /** Fresh payload: rebuild the planet toggles and run the default search. */
  const initialisedDataRef = useRef<AstroLinesResponse | null>(null);
  useEffect(() => {
    if (initialisedDataRef.current === data) return;
    initialisedDataRef.current = data;
    if (!planets.length) return;

    const visibility: Record<string, boolean> = {};
    planets.forEach((planet) => {
      visibility[planet] = true;
    });
    setPlanetVisibility(visibility);

    const nextPlanet = planets.includes(DEFAULT_PLANET)
      ? DEFAULT_PLANET
      : planets[0];
    setSelectedPlanet(nextPlanet);
    runSearch(nextPlanet, selectedAngle, radiusKm);
  }, [data, planets, runSearch, selectedAngle, radiusKm]);

  /** Clicking a line on the map selects it, then searches with that selection. */
  useEffect(() => {
    if (!pendingSearch) return;
    setPendingSearch(false);
    runSearch(selectedPlanet, selectedAngle, radiusKm);
  }, [pendingSearch, runSearch, selectedPlanet, selectedAngle, radiusKm]);

  const handleFindCities = useCallback(() => {
    runSearch(selectedPlanet, selectedAngle, radiusKm);
  }, [runSearch, selectedPlanet, selectedAngle, radiusKm]);

  const handleClearCities = useCallback(() => {
    setCityHits(undefined);
    clearCityResults();
  }, [clearCityResults]);

  /* ------------------------------ visibility ------------------------------ */

  const handleLayerVisibilityChange = useCallback(
    (category: AstroLineCategory, value: boolean) => {
      setLayerVisibility((previous) => ({ ...previous, [category]: value }));
    },
    [],
  );

  const handlePlanetVisibilityChange = useCallback(
    (planet: string, value: boolean) => {
      setPlanetVisibility((previous) => ({ ...previous, [planet]: value }));
    },
    [],
  );

  const handleAllPlanetsVisible = useCallback(
    (value: boolean) => {
      const visibility: Record<string, boolean> = {};
      planets.forEach((planet) => {
        visibility[planet] = value;
      });
      setPlanetVisibility(visibility);
    },
    [planets],
  );

  /* --------------------------- fullscreen + dock -------------------------- */

  const {
    isFullscreen,
    isSupported: isFullscreenSupported,
    toggle: toggleFullscreen,
  } = useFullscreen(stageRef, invalidateSize);

  /**
   * While fullscreen, MUI portals must live inside the fullscreen element or
   * the browser never paints them — see createAstroTheme for the details.
   * Held in state rather than read from the ref during render, so the theme is
   * rebuilt when the element becomes available.
   */
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null,
  );
  useEffect(() => {
    setPortalContainer(isFullscreen ? stageRef.current : null);
  }, [isFullscreen]);

  const scopedTheme = useMemo(
    () => createAstroTheme(portalContainer),
    [portalContainer],
  );

  const handleSectionChange = useCallback((id: AstroSectionId) => {
    setExpandedSection((previous) => (previous === id ? false : id));
  }, []);

  useEffect(() => {
    // Re-measure once the nav and the drawer have settled into layout — the
    // drawer changes how much of the stage the map actually gets.
    const frame = window.requestAnimationFrame(invalidateSize);
    return () => window.cancelAnimationFrame(frame);
  }, [isDockCollapsed, invalidateSize]);

  const getOverlay = () => {
    if (isLinesLoading) {
      return { title: t("astroLoadingLines"), message: "", icon: undefined };
    }
    if (hasLinesError) {
      return {
        title: t("astroLinesLoadError"),
        message: linesError || t("astroLinesLoadErrorMessage"),
        icon: <ErrorOutlineRoundedIcon />,
      };
    }
    if (!selectedProfile) {
      return {
        title: t("astroSelectProfileTitle"),
        message: t("astroSelectProfileMessage"),
        icon: <PersonSearchOutlinedIcon />,
      };
    }
    if (!lines.length) {
      return {
        title: t("astroNoLinesTitle"),
        message: t("astroNoLinesMessage"),
        icon: <ExploreOutlinedIcon />,
      };
    }
    return { title: "", message: "", icon: undefined };
  };

  const overlay = getOverlay();

  const visiblePlanetCount = planets.filter(
    (planet) => planetVisibility[planet] !== false,
  ).length;
  const activeLayerCount = (
    ["main", "harm", "disharm"] as AstroLineCategory[]
  ).filter((category) => layerVisibility[category]).length;

  const getNav = () => (
    <Box sx={classes.navWrapper}>
      <Box sx={classes.navRow}>
        <Tooltip title={t("astroBack")}>
          <Box
            role="button"
            tabIndex={0}
            onClick={() => history.goBack()}
            sx={classes.navBack}
          >
            <ArrowBackIcon sx={{ fontSize: "15px" }} />
          </Box>
        </Tooltip>

        <Box sx={classes.navSeparator} />

        <Box sx={classes.navBrand}>
          <Avatar variant="rounded" sx={classes.navMark}>
            ✦
          </Avatar>
          <Stack sx={{ minWidth: 0 }}>
            <Typography sx={classes.navTitle}>
              {t("astrocartography")}
            </Typography>
            <Typography variant="caption" noWrap sx={classes.navSubtitle}>
              {t("astroPageSubtitle")}
            </Typography>
          </Stack>
        </Box>

        <Box sx={classes.navSpacer} />

        {Boolean(planets.length) && (
          <Box sx={classes.navLegend}>
            <Stack direction="row" spacing={0.6} alignItems="center">
              <PublicOutlinedIcon
                sx={{ fontSize: "15px", color: ASTRO_ACCENTS.planets.main }}
              />
              <Typography
                sx={{
                  ...classes.navLegendValue,
                  color: ASTRO_ACCENTS.planets.main,
                }}
              >
                {`${visiblePlanetCount}/${planets.length}`}
              </Typography>
              <Typography variant="overline" color="text.secondary">
                {t("astroLegendPlanets")}
              </Typography>
            </Stack>
            <Divider
              orientation="vertical"
              flexItem
              sx={classes.navLegendDivider}
            />
            <Stack direction="row" spacing={0.6} alignItems="center">
              <LayersOutlinedIcon
                sx={{ fontSize: "15px", color: ASTRO_ACCENTS.layers.main }}
              />
              <Typography
                sx={{
                  ...classes.navLegendValue,
                  color: ASTRO_ACCENTS.layers.main,
                }}
              >
                {`${activeLayerCount}/3`}
              </Typography>
              <Typography variant="overline" color="text.secondary">
                {t("astroLegendLayers")}
              </Typography>
            </Stack>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <ThemeProvider theme={scopedTheme}>
      <Box sx={classes.pageContainer}>
        {getNav()}

        <AstroMapStage
          containerRef={containerRef}
          stageRef={stageRef}
          overlayIcon={overlay.icon}
          overlayTitle={overlay.title}
          overlayMessage={overlay.message}
          isLoading={isLinesLoading}
          isFullscreen={isFullscreen}
          isFullscreenSupported={isFullscreenSupported}
          onToggleFullscreen={toggleFullscreen}
        >
          <AstroControlDock
            expandedSection={expandedSection}
            onSectionChange={handleSectionChange}
            isCollapsed={isDockCollapsed}
            onCollapsedChange={setIsDockCollapsed}
            profiles={profiles}
            selectedProfile={selectedProfile}
            onProfileChange={setSelectedProfile}
            isProfilesLoading={isProfilesLoading}
            birthPlace={data?.birthPlace}
            baseMapKey={baseMapKey}
            onBaseMapChange={setBaseMapKey}
            planets={planets}
            planetColors={planetColors}
            selectedPlanet={selectedPlanet}
            onPlanetChange={setSelectedPlanet}
            selectedAngle={selectedAngle}
            onAngleChange={setSelectedAngle}
            radiusKm={radiusKm}
            onRadiusChange={setRadiusKm}
            onFindCities={handleFindCities}
            onClearCities={handleClearCities}
            isSearchDisabled={!selectedPlanet || isLinesLoading}
            cityHits={cityHits}
            searchedPlanet={searchedPlanet}
            searchedAngle={searchedAngle}
            searchedRadiusKm={searchedRadiusKm}
            layerVisibility={layerVisibility}
            onLayerVisibilityChange={handleLayerVisibilityChange}
            showLabels={showLabels}
            onShowLabelsChange={setShowLabels}
            planetVisibility={planetVisibility}
            onPlanetVisibilityChange={handlePlanetVisibilityChange}
            onAllPlanetsVisible={handleAllPlanetsVisible}
          />
        </AstroMapStage>
      </Box>
    </ThemeProvider>
  );
};

export default Astrocartography;
